import { moveInOrOut } from '../action';
import { drawAlignmentHelper, getOrCreateSnapEngine } from '../lib/align';
import {
  clipEventActions,
  getInteractiveImageClipHandlers,
  renderClipOverlay,
} from '../lib/image-clip';
import { drawRectRegion, drawRegion, renderInteractiveNodeBorder } from '../lib/interaction/label';
import {
  addPointAtSegment,
  getInteractiveLineHandlers,
  hitTestLineNode,
  renderInteractiveLineBorder,
} from '../lib/interaction/line';
import { moveAction, regionMoveAction } from '../lib/interaction/move';
import { getCurrentSelectableNodes } from '../lib/interaction/node';
import {
  addPathPointAtSegment,
  getInteractivePathHandlers,
  pathEditAction,
} from '../lib/interaction/path';
import {
  getInteractiveRadiusHandlers,
  isRadiusableNode,
  radiusAction,
  renderRadiusHandler,
} from '../lib/interaction/radius';
import {
  renderEdgeHandler,
  renderInteractiveGroupBorder,
  renderLineMidPointHandler,
  renderLinePointHandler,
  renderPathAnchorHandler,
  renderPathControlHandler,
  renderPointHandler,
  renderRotateHandler,
} from '../lib/interaction/render';
import {
  drawScrollbars,
  eventToScreenPoint,
  getScrollbarHandlers,
  scrollbarMoveAction,
} from '../lib/interaction/scrollbar';
import {
  getSectionMoveHandlers,
  renderSectionMoveHandler,
  sectionMoveAction,
} from '../lib/interaction/section-move';
import {
  drawSmartLayoutButtons,
  getSmartLayoutHandlers,
  smartLayoutAction,
} from '../lib/interaction/smart-layout';
import { drawMeasureGuides } from '../lib/interaction/measure';
import { styleBrushAction } from '../lib/interaction/style-brush';
import {
  captureMoveDistanceAction,
  captureStateAction,
  doubleClickAction,
  editTextAction,
  hoverAction,
  hoverHandlerAction,
  insertAction,
  moveModeAction,
  regionResizeAction,
  regionRotateAction,
  resizeAction,
  selectAction,
  selectByRegionAction,
  textSelectionAction,
  unselectAction,
} from '../lib/interactive';
import { Rect, Region } from '../lib/region';
import {
  drawSpacingHandles,
  getSpacingHandlers,
  spacingAdjustAction,
  spacingEqualizeAction,
} from '../lib/spacing';
import { EventTypes, LayoutSizeType, TYPES } from '../render/constants';

export function createInteractive(renderContext) {
  const canvas = renderContext.context.canvas;
  const { cursor } = renderContext.providers;
  const interactive = renderContext.interactive;
  interactive.cursor = cursor;
  interactive.render = renderInteractive;
  interactive.stageContext = renderContext;
  interactive.requestFocus = () => {
    canvas.parentNode?.focus();
  };

  renderContext.providers.interactive = interactive;
  interactive.dispose = () => {};
  interactive.selectMode = 'default';
  interactive.createHitTestContext = createHitTestContext;
  interactive.screenX = 0;
  interactive.screenY = 0;
  // mobile touch flags
  interactive.isTouchEditingOrDragging = false;
  let longPressTimer = null;
  let touchStartPoint = null;
  const LONG_PRESS_MS = 450;
  const MOVE_TOLERANCE = 6 * renderContext.dpr;

  interactive.getMovingNodeInitialBox = (id) => {
    const node = renderContext.api.getNode(id);
    interactive.cacheMovingNode = interactive.cacheMovingNode || {};
    const exsit = interactive.cacheMovingNode[id];
    if (exsit) return exsit;
    const cache = {
      ...node,
    };
    interactive.cacheMovingNode[id] = cache;
    return cache;
  };

  // Clip mode state and helpers
  interactive.clipMode = null;
  interactive.enterClipMode = (node) => {
    if (!node || node.type !== TYPES.image) return;
    interactive.clipMode = {
      nodeId: node.id,
      state: {
        startPoint: null,
        cache: null,
        resizeEdge: null,
      },
    };
    interactive.cursor.set('');
    renderContext.api.markNeedRender();
    renderContext.hooks.onNodeMoveCallback();
  };
  interactive.exitClipMode = () => {
    if (interactive.clipMode) {
      interactive.clipMode = null;
      interactive.clipDrag = null;
      interactive.cursor.set('');
      renderContext.api.markNeedRender();
    }
  };

  // Clip mode state and helpers
  interactive.pathMode = null;
  interactive.enterPathMode = (node) => {
    if (!node || node.type !== TYPES.path) return;
    interactive.pathMode = {
      nodeId: node.id,
      state: {
        startPoint: null,
        cache: null,
        resizeEdge: null,
      },
    };
    interactive.cursor.set('');
    renderContext.api.markNeedRender();
    renderContext.hooks.onNodeMoveCallback();
  };

  interactive.exitPathMode = () => {
    if (interactive.pathMode) {
      interactive.pathMode = null;
    }
  };

  interactive.exitEditMode = () => {
    interactive.exitClipMode();
    interactive.exitPathMode();
    // also exit style brush mode
    interactive.brush = null;
  };

  // ESC to exit clip mode
  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      interactive.exitEditMode();
    }
  };
  window.addEventListener('keydown', onKeyDown);

  interactive.eventToPoint = eventToPoint;
  function eventToPoint(e) {
    const screenPoint = eventToScreenPoint(renderContext, e);
    const { scale: scaleProvider } = renderContext.providers;
    return scaleProvider.offsetTransform(screenPoint);
  }

  canvas.addEventListener('mousedown', (e) => {
    // 不要阻止默认，交由 action pipeline 统一处理
    // 仅当未在编辑文本时才把焦点给容器，避免隐藏输入框失焦导致选区丢失
    try {
      const screenPoint = eventToScreenPoint(renderContext, e);
      const point = renderContext.providers.scale.offsetTransform(screenPoint);
      const node = getTextNodeAtPoint(renderContext, point);
      const isTextEditing = renderContext.providers.text.isEditing(node);
      if (!node) {
        // 点击非文字区域时，确保容器获得焦点
        interactive.requestFocus();
      } else if (renderContext.providers.text.isEditing(node)) {
        // 点击正在编辑的文字时，保持/恢复隐藏输入焦点
        renderContext.providers.text?.focus();
      } else {
        // 点击文字节点但尚未进入编辑，不要抢占焦点
        // 让后续的 up/双击逻辑决定是否进入编辑并聚焦隐藏输入
      }
      if (isTextEditing && e.button === 2) {
        renderContext.providers.text.cancel();
        return;
      }
      if (e.button === 2) {
        return;
      }
    } catch (err) {
      console.error(err);
      interactive.requestFocus();
    }

    eventHandler(e, 'down');
  });

  // Touch → mouse-like events mapping for mobile editing
  canvas.addEventListener(
    'touchstart',
    (e) => {
      if (e.touches.length > 1) {
        // Let pinch/pan be handled by scale provider
        interactive.isTouchEditingOrDragging = false;
        clearLongPress();
        renderContext.providers.scale.isDisabled = false;
        return;
      }
      const t = e.touches[0];
      const fake = { clientX: t.clientX, clientY: t.clientY, target: canvas };
      touchStartPoint = eventToScreenPoint(renderContext, fake);
      interactive.isTouchEditingOrDragging = false;
      renderContext.providers.scale.isDisabled = true;
      startLongPress(e, fake);
      eventHandler(fake, 'down');
      // prevent native scroll
      e.preventDefault();
    },
    { passive: false },
  );

  canvas.addEventListener(
    'touchmove',
    (e) => {
      if (e.touches.length > 1) {
        // Multi-touch is managed by scale provider; cancel long-press
        clearLongPress();
        interactive.isTouchEditingOrDragging = false;
        return;
      }
      const t = e.touches[0];
      const fake = { clientX: t.clientX, clientY: t.clientY, target: canvas };
      const current = eventToScreenPoint(renderContext, fake);
      if (
        touchStartPoint &&
        (Math.abs(current.x - touchStartPoint.x) > MOVE_TOLERANCE ||
          Math.abs(current.y - touchStartPoint.y) > MOVE_TOLERANCE)
      ) {
        // Moving cancels long press
        clearLongPress();
      }
      eventHandler(fake, 'move');
      // Mark dragging state when moving handlers/nodes
      interactive.isTouchEditingOrDragging = !!(
        interactive.movingHandler ||
        (interactive.movingNodes && interactive.movingNodes.length > 0)
      );
      e.preventDefault();
    },
    { passive: false },
  );

  canvas.addEventListener(
    'touchend',
    (e) => {
      // Use changedTouches for the last point
      const t = e.changedTouches && e.changedTouches[0];
      if (t) {
        const fake = { clientX: t.clientX, clientY: t.clientY, target: canvas };
        eventHandler(fake, 'up', true);
      } else {
        eventHandler({ pageX: 0, pageY: 0, target: canvas }, 'up', true);
      }
      clearLongPress();
      interactive.isTouchEditingOrDragging = false;
      // Re-enable stage panning/zoom after short delay to avoid flicker
      setTimeout(() => {
        renderContext.providers.scale.isDisabled = false;
      }, 0);
      e.preventDefault();
    },
    { passive: false },
  );

  let lastDragOver = null;
  canvas.addEventListener('dragover', (e) => {
    const apiNow =
      renderContext && renderContext.api && typeof renderContext.api.now === 'function'
        ? () => renderContext.api.now()
        : () => (performance && performance.now ? performance.now() : Date.now());
    if (lastDragOver && lastDragOver > apiNow() - 16) {
      return;
    }
    lastDragOver = apiNow();
    const point = eventToPoint(e);

    if (interactive.createNode) {
      if (!interactive.pointStart) {
        interactive.pointStart = point;
        const nodeData = interactive.createNode;
        let node = renderContext.api.getNode(nodeData.id);
        if (node) {
          renderContext.api.updateNode(node.id, {
            x: point.x - nodeData.w / 2,
            y: point.y - nodeData.h / 2,
          });
        } else {
          node = renderContext.api.addNode({
            ...interactive.createNode,
            x: point.x - nodeData.w / 2,
            y: point.y - nodeData.h / 2,
          });
        }
        renderContext.api.selectNode([node.id]);
        interactive.createNode = node;
        if (interactive.createNodeCallback) {
          interactive.createNodeCallback();
        }
        interactive.createNodeCallback = null;
      }
      const pointStart = interactive.pointStart;
      const node = interactive.createNode;
      const cache = interactive.getMovingNodeInitialBox(node.id);
      renderContext.api.updateNode(node.id, {
        x: cache.x + point.x - pointStart.x,
        y: cache.y + point.y - pointStart.y,
      });
      renderContext.hooks.onNodeMoveCallback();
    }
  });

  window.addEventListener('dragend', onDragEnd);
  window.addEventListener('mouseup', onMouseUp);
  window.addEventListener('mousemove', onMouseMove);

  function onDragEnd(e) {
    if (interactive.createNode) {
      const node = interactive.createNode;
      const point = eventToPoint(e);
      const hitTestContext = createHitTestContext(
        point,
        eventToScreenPoint(renderContext, e),
        EventTypes.up,
        renderContext,
      );
      const movingNodes = [
        {
          id: node.id,
          x: node.x,
          y: node.y,
          w: node.w,
          h: node.h,
          node: node,
        },
      ];

      moveInOrOut(hitTestContext, movingNodes);
      if (node.type === TYPES.text) {
        renderContext.providers.text.create(node);
      } else {
        interactive.requestFocus();
      }
    } else {
      interactive.requestFocus();
    }
    cleanup();
  }

  function onMouseUp(e) {
    const className = e.target && e.target.className;
    // Handle both string className and SVGAnimatedString (for SVG elements)
    const classNameString = typeof className === 'string' ? className : className?.baseVal || '';
    const fromStage = e.target && classNameString && classNameString.includes('seede-stage');
    eventHandler(e, 'up', fromStage);
  }

  function onMouseMove(e) {
    // 不要手动处理文字拖拽，交给 textSelectionAction
    eventHandler(e, 'move');
  }

  function cleanup() {
    const { interactive } = renderContext;
    interactive.movingNodes = [];
    interactive.previousSelectedNodes = [];
    interactive.previousPointDownStart = null;
    interactive.pointStart = null;
    interactive.maxMoveDistance = null;
    interactive.rotationState = null;
    interactive.isRealMove = null;
    interactive.areaStart = null;
    interactive.movingHandler = null;
    interactive.alignmentResult = null;
    interactive.createNode = null;
    interactive.altCreated = false;
    interactive.skipSelectFromUp = false;
    interactive.insertedNode = null;
    interactive.cacheMovingNode = {};
    interactive.moveOutCache = null;
    interactive.alignCache = null;
    // Ensure SnapEngine state is cleared between gestures
    try {
      getOrCreateSnapEngine(renderContext).end();
    } catch {}
    interactive.screenX = 0;
    interactive.screenY = 0;
    interactive.cursor.reset();
    interactive.isTouchEditingOrDragging = false;
    clearLongPress();
    // 结束文字拖拽选区
    renderContext.providers.text?.endSelection?.();
    interactive.spacingState = null;
    renderContext.hooks.onNodeMoveCallback();
  }

  function renderInteractive(renderContext) {
    const context = renderContext.context;
    const { selection } = renderContext.providers;
    const selectedNodes = renderContext.api.getSelectedNodes();
    const isMultiSelect = selectedNodes.length > 1;

    context.save();
    if (!isMultiSelect) {
      selectedNodes.map((node) => {
        const parentNode = renderContext.api.getNode(node.parentId);
        if (parentNode?.type === TYPES.group) {
          renderInteractiveGroupBorder(renderContext, parentNode);
        }
        renderInteractiveBorder(context, node, true);
      });
    }
    selection.hovers.map((node) => {
      renderInteractiveBorder(context, node);
    });

    drawSelectionArea(context, renderContext, selectedNodes);
    drawAlignmentHelper(context, renderContext);

    if (!isMultiSelect) {
      selectedNodes.map((node) => {
        if (renderContext.api.shouldSkip(node)) return;
        renderInteractiveHandler(context, node, renderContext);
      });
    }

    // spacing and smart-layout handles are drawn inside drawSelectionArea()
    drawScrollbars(context, renderContext);
    drawMeasureGuides(context, renderContext);

    // Clip-mode overlay with custom handlers
    if (interactive.clipMode) {
      const node = renderContext.api.getNode(interactive.clipMode.nodeId);
      if (node) {
        renderClipOverlay(renderContext, node);
      }
    }

    context.restore();
  }

  function renderInteractiveBorder(context, node, labelSize, options) {
    // 特殊处理Line节点的边框
    if (node.type === TYPES.line) {
      return renderInteractiveLineBorder(renderContext, context, node, labelSize, options);
    }

    return renderInteractiveNodeBorder(renderContext, context, node, labelSize, options);
  }

  function eventHandler(e, type, fromStage = false) {
    renderContext.providers.selection.clearHover();
    renderContext.interactive.hoverHandlerId = '';
    if (renderContext.mode === 'presentation') return;

    const screenPoint = eventToScreenPoint(renderContext, e);
    const point = renderContext.providers.scale.offsetTransform(screenPoint);
    point.fromStage = fromStage;

    const hitTestContext = createHitTestContext(point, screenPoint, type, renderContext);

    // 处理右键点击
    if (type === EventTypes.down && e.button === 2) {
      if (handleLineRightClick(hitTestContext) || handlePathRightClick(hitTestContext)) {
        e.preventDefault();
        return;
      }
    }

    const eventActions = [
      captureMoveDistanceAction,
      scrollbarMoveAction,
      clipEventActions,
      pathEditAction,
      sectionMoveAction,
      spacingAdjustAction,
      spacingEqualizeAction,
      smartLayoutAction,
      captureStateAction,
      moveModeAction,
      textSelectionAction,
      editTextAction,
      hoverAction,
      hoverHandlerAction,
      radiusAction,
      styleBrushAction,
      insertAction,
      doubleClickAction,
      regionRotateAction,
      regionMoveAction,
      regionResizeAction,
      resizeAction,
      moveAction,
      selectByRegionAction,
      selectAction,
      unselectAction,
    ];
    for (const action of eventActions) {
      if (action(hitTestContext)) {
        break;
      }
    }

    if (type === EventTypes.up) {
      cleanup();
    }

    renderContext.api.markNeedRender();
  }

  return () => {
    window.removeEventListener('mouseup', onMouseUp);
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('dragend', onDragEnd);
    window.removeEventListener('keydown', onKeyDown);
    cursor.detach();
  };

  function startLongPress(nativeEvent, fakePointEvent) {
    clearLongPress();
    longPressTimer = setTimeout(() => {
      // Emulate a context action for Line/Path midpoint add on long press
      const screenPoint = eventToScreenPoint(renderContext, fakePointEvent);
      const point = renderContext.providers.scale.offsetTransform(screenPoint);
      const ctx = createHitTestContext(point, screenPoint, EventTypes.down, renderContext);
      if (handleLineRightClick(ctx) || handlePathRightClick(ctx)) {
        renderContext.api.markNeedRender();
      }
    }, LONG_PRESS_MS);
  }

  function clearLongPress() {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      longPressTimer = null;
    }
  }
}

function getTextNodeAtPoint(renderContext, point) {
  const nodes = renderContext.api.getNodes().slice().reverse();
  for (const n of nodes) {
    if (n.type !== TYPES.text) continue;
    if (!renderContext.api.shouldSkip(n)) {
      if (renderContext.api.getNodeRegion(n).contains(point.x, point.y)) return n;
    }
  }
  return null;
}

function hitTestNodeHandler(renderContext, point, node, region) {
  const handlers = getInteractiveHandlers(renderContext, node, region);
  for (const handler of handlers) {
    if (handler.contains(point.x, point.y)) {
      handler.node = node;
      return handler;
    }
  }
  return null;
}

export function getInteractiveHandlers(stageContext, node, region) {
  region = region || stageContext.api.getNodeRegion(node);
  const { scale: scaleProvider, text, interactive } = stageContext.providers;
  if (text && text.isEditing(node)) return [];
  const scale = 1 / scaleProvider.scale;

  // 特殊处理Line节点
  if (node?.type === TYPES.line) {
    return getInteractiveLineHandlers(stageContext, node, region, scale);
  }

  // Path 节点（仅直线锚点模式）
  if (node?.type === TYPES.path && interactive.pathMode) {
    return [...getInteractivePathHandlers(stageContext, node, region, scale)];
  }

  // Frame 也支持旋转手柄（与 Figma 一致）
  if (node?.type === TYPES.frame) {
    return [...getInteractiveEdgeHandlers(region, node, scale)];
  }

  if (node?.type === TYPES.image && interactive.clipMode) {
    const clipHandlers = getInteractiveImageClipHandlers(stageContext, node);
    return clipHandlers;
  }

  // 当传入的是选区区域（node为空或为selection）时，增加"间距手柄"命中
  const isSelectionRegion = !node || !node.id;
  const baseHandlers = [
    ...getInteractiveEdgeHandlers(region, node, scale),
    ...getInteractivePointHandlers(region, scale),
    ...getRotateHandlers(region, node || { type: 'selection' }, scale),
  ];
  // 添加 section-move 句柄（支持单选与多选selection区域）
  const sectionHandlers = getSectionMoveHandlers(
    stageContext,
    node || { type: 'selection' },
    region,
    scale,
  );
  if (sectionHandlers && sectionHandlers.length) {
    baseHandlers.push(...sectionHandlers);
  }
  // Add radius handlers for rectangle shapes
  if (isRadiusableNode(node)) {
    return [...baseHandlers, ...getInteractiveRadiusHandlers(stageContext, node, region, scale)];
  }
  if (isSelectionRegion && stageContext.api.getSelectedNodes().length > 1) {
    return [
      ...baseHandlers,
      ...getSpacingHandlers(stageContext, region, scale),
      ...getSmartLayoutHandlers(stageContext),
    ];
  }
  return baseHandlers;
}

const defaultPointTypes = ['topLeft', 'topRight', 'bottomLeft', 'bottomRight'];

function getInteractivePointHandlers(region, scale = 1) {
  let pointTypes = defaultPointTypes;
  if (region.rect.w / scale < 64 || region.rect.h / scale < 64) {
    pointTypes = ['bottomRight'];
  }

  const rotatedControlPoints = pointTypes.map((key) => {
    const point = region[key];
    return {
      ...point,
      id: key,
      type: 'point',
    };
  });

  const controlPointSize = 14 * scale;
  return rotatedControlPoints.map((point) => {
    const rect = new Rect(
      point.x - controlPointSize,
      point.y - controlPointSize,
      controlPointSize * 2,
      controlPointSize * 2,
    );
    rect.id = point.id;
    rect.type = point.type;
    return rect;
  });
}

const defaultMidPoints = ['top', 'left', 'bottom', 'right'];

// 仅一个旋转手柄，位于上边中点外侧，常显
function getRotateHandlers(region, node, scale = 1) {
  if (!node) return [];
  if (node.type === TYPES.frame) return [];

  const hitRadius = 28 * scale;
  const visualRadius = 20 * scale;
  const offset = 56 * scale;

  // 判断元素的宽高比，决定旋转手柄位置
  const box = region.rect;
  const width = box.w;
  const height = box.h;
  const isSelection = region.type === 'selection' || node.type === 'selection';

  let anchorPoint;

  if (isSelection || Math.abs(width - height) < 2) {
    // 宽度大于等于高度，位于下方
    anchorPoint = {
      x: (region.bottomLeft.x + region.bottomRight.x) / 2,
      y: (region.bottomLeft.y + region.bottomRight.y) / 2,
    };
  } else {
    // 高度大于宽度，位于右侧
    anchorPoint = {
      x: (region.topRight.x + region.bottomRight.x) / 2,
      y: (region.topRight.y + region.bottomRight.y) / 2,
    };
  }

  // 使用中心到锚点的方向，确保手柄始终在外侧
  const cx = region.center.x;
  const cy = region.center.y;
  const vx = anchorPoint.x - cx;
  const vy = anchorPoint.y - cy;
  const vlen = Math.hypot(vx, vy) || 1;
  const nx = vx / vlen;
  const ny = vy / vlen;

  const hx = anchorPoint.x + nx * offset;
  const hy = anchorPoint.y + ny * offset;

  const rect = new Rect(hx - hitRadius, hy - hitRadius, hitRadius * 2, hitRadius * 2);
  rect.id = 'rotate';
  rect.type = 'rotate';
  rect.anchor = anchorPoint;
  rect.visualRadius = visualRadius;
  return [rect];
}

function getInteractiveEdgeHandlers(region, node, scale = 1) {
  // Frame: use outside-centered, semi-transparent black rounded-rect handlers
  if (node && node.type === TYPES.frame) {
    let pointTypes = defaultMidPoints;
    if (region.rect.w / scale < 70 || region.rect.h / scale < 70) {
      pointTypes = ['right', 'bottom'];
    }

    function getAngle(p1, p2) {
      return (Math.atan2(p2.y - p1.y, p2.x - p1.x) * 180) / Math.PI;
    }

    const topMid = {
      x: (region.topLeft.x + region.topRight.x) / 2,
      y: (region.topLeft.y + region.topRight.y) / 2,
      id: 'top',
      type: 'edge',
      angle: getAngle(region.topLeft, region.topRight),
    };
    const rightMid = {
      x: (region.topRight.x + region.bottomRight.x) / 2,
      y: (region.topRight.y + region.bottomRight.y) / 2,
      vertical: true,
      id: 'right',
      type: 'edge',
      angle: getAngle(region.topRight, region.bottomRight) + 90,
    };
    const bottomMid = {
      x: (region.bottomLeft.x + region.bottomRight.x) / 2,
      y: (region.bottomLeft.y + region.bottomRight.y) / 2,
      id: 'bottom',
      type: 'edge',
      angle: getAngle(region.bottomLeft, region.bottomRight),
    };
    const leftMid = {
      x: (region.topLeft.x + region.bottomLeft.x) / 2,
      y: (region.topLeft.y + region.bottomLeft.y) / 2,
      vertical: true,
      id: 'left',
      type: 'edge',
      angle: getAngle(region.topLeft, region.bottomLeft) + 90,
    };

    let midControlPoints = [topMid, rightMid, bottomMid, leftMid].filter((item) =>
      pointTypes.includes(item.id),
    );

    if (node && node.height === LayoutSizeType.hug) {
      midControlPoints = midControlPoints.filter(
        (item) => item.id !== 'top' && item.id !== 'bottom',
      );
    }

    const rectWidth = 64 * scale;
    const rectHeight = 10 * scale;
    const cx = region.center.x;
    const cy = region.center.y;

    // Move handle center slightly outward from the edge midpoint
    const outwardGap = 12 * scale;

    return midControlPoints.map((point) => {
      const vx = point.x - cx;
      const vy = point.y - cy;
      const len = Math.hypot(vx, vy) || 1;
      const nx = vx / len;
      const ny = vy / len;

      // Use half thickness along normal plus a small gap to ensure fully outside
      const normalThickness = rectHeight; // consistent outward offset
      const dist = normalThickness / 2 + outwardGap;

      const hx = point.x + nx * dist;
      const hy = point.y + ny * dist;

      let rect;
      if (point.vertical) {
        rect = new Rect(hx - rectHeight / 2, hy - rectWidth / 2, rectHeight, rectWidth);
        rect = Region.fromRect(rect).rotate(point.angle);
      } else {
        rect = new Rect(hx - rectWidth / 2, hy - rectHeight / 2, rectWidth, rectHeight);
        rect = Region.fromRect(rect).rotate(point.angle);
      }

      rect.id = point.id;
      rect.type = point.type;
      rect.style = 'frameEdge';

      return rect;
    });
  }

  let pointTypes = defaultMidPoints;
  if (region.rect.w / scale < 70 || region.rect.h / scale < 70) {
    pointTypes = ['right', 'bottom'];
  }

  // 计算每个边的角度
  function getAngle(p1, p2) {
    return (Math.atan2(p2.y - p1.y, p2.x - p1.x) * 180) / Math.PI;
  }

  // 四个边的中点和角度
  const topMid = {
    x: (region.topLeft.x + region.topRight.x) / 2,
    y: (region.topLeft.y + region.topRight.y) / 2,
    id: 'top',
    type: 'edge',
    angle: getAngle(region.topLeft, region.topRight),
  };
  const rightMid = {
    x: (region.topRight.x + region.bottomRight.x) / 2,
    y: (region.topRight.y + region.bottomRight.y) / 2,
    vertical: true,
    id: 'right',
    type: 'edge',
    // 右侧控制点应垂直于边，即加90度
    angle: getAngle(region.topRight, region.bottomRight) + 90,
  };
  const bottomMid = {
    x: (region.bottomLeft.x + region.bottomRight.x) / 2,
    y: (region.bottomLeft.y + region.bottomRight.y) / 2,
    id: 'bottom',
    type: 'edge',
    angle: getAngle(region.bottomLeft, region.bottomRight),
  };
  const leftMid = {
    x: (region.topLeft.x + region.bottomLeft.x) / 2,
    y: (region.topLeft.y + region.bottomLeft.y) / 2,
    vertical: true,
    id: 'left',
    type: 'edge',
    // 左侧控制点应垂直于边，即加90度
    angle: getAngle(region.topLeft, region.bottomLeft) + 90,
  };

  let midControlPoints = [topMid, rightMid, bottomMid, leftMid].filter((item) =>
    pointTypes.includes(item.id),
  );

  if (node && node.height === LayoutSizeType.hug) {
    midControlPoints = midControlPoints.filter((item) => item.id !== 'top' && item.id !== 'bottom');
  }

  const rectWidth = 32 * scale;
  const rectHeight = 14 * scale;

  return midControlPoints.map((point) => {
    let rect;
    // 垂直方向的控制点需要加90度旋转
    if (point.vertical) {
      rect = new Rect(point.x - rectHeight / 2, point.y - rectWidth / 2, rectHeight, rectWidth);
      rect = Region.fromRect(rect).rotate(point.angle);
    } else {
      rect = new Rect(point.x - rectWidth / 2, point.y - rectHeight / 2, rectWidth, rectHeight);
      rect = Region.fromRect(rect).rotate(point.angle);
    }

    rect.id = point.id;
    rect.type = point.type;

    return rect;
  });
}

function drawSelectionArea(context, stageContext, selectedNodes) {
  const { interactive, scale } = stageContext.providers;
  if (interactive.areaStart && interactive.areaEnd) {
    const startX = interactive.areaStart.x;
    const startY = interactive.areaStart.y;
    const endX = interactive.areaEnd.x;
    const endY = interactive.areaEnd.y;
    const width = endX - startX;
    const height = endY - startY;
    const rect = new Rect(startX, startY, width, height);
    const box = scale.boxTransform(rect);
    drawRectRegion(context, box, { lineWidth: 2 });
    selectedNodes.map((node) => {
      renderInteractiveNodeBorder(stageContext, context, node, true, { lineWidth: 2 });
    });
  } else if (selectedNodes.length > 1) {
    const rect = stageContext.api.getSelectedNodesRegion();
    const box = scale.boxTransform(rect);
    drawRectRegion(context, box, { fill: 'transparent', lineWidth: 2 });
    selectedNodes.map((node) => {
      renderInteractiveNodeBorder(stageContext, context, node, true, { lineWidth: 2 });
    });
    const region = Region.fromRect(box);
    region.type = 'selection';
    renderInteractiveHandler(context, box, stageContext, { region });
    drawSpacingHandles(context, stageContext);
    drawSmartLayoutButtons(context, stageContext);
  }
}

function renderInteractiveHandler(context, node, stageContext, { region } = {}) {
  const { scale, interactive, text } = stageContext.providers;
  if (text && text.isEditing(node)) return;
  region =
    region ||
    stageContext.api.getNodeRegion(node).transform(scale.scale, scale.translateX, scale.translateY);

  // 特殊处理Line节点
  if (node.type === TYPES.line) {
    const lineHandlers = getInteractiveLineHandlers(stageContext, node, region);
    lineHandlers.forEach((area) => {
      const isActive = area.id === interactive.hoverHandlerId;
      if (area.type === 'linePoint') {
        renderLinePointHandler(context, area, isActive);
      } else if (area.type === 'lineMidPoint') {
        renderLineMidPointHandler(context, area, isActive);
      }
    });
    return;
  }

  // Path 节点的点编辑模式（支持曲线控制点）
  if (node.type === TYPES.path && interactive.pathMode) {
    const pathHandlers = getInteractivePathHandlers(stageContext, node, region);
    pathHandlers.forEach((area) => {
      const isActive = area.id === interactive.hoverHandlerId;
      if (area.type === 'pathControl') {
        renderPathControlHandler(context, area, isActive);
      } else if (area.type === 'pathPoint') {
        renderPathAnchorHandler(context, area, isActive);
      } else if (area.type === 'pathMidPoint') {
        renderLineMidPointHandler(context, area, isActive);
      }
    });
    return;
  }

  if (node.type === TYPES.image && interactive.clipMode) {
    return;
  }

  const isMoving = interactive.movingNodes && interactive.movingNodes.length;
  if (isMoving) {
    return;
  }

  if (node.type !== TYPES.frame) {
    const controlPoints = getInteractivePointHandlers(region);
    controlPoints.forEach((area) => {
      if (interactive.movingHandler) {
        const isActive = area.id === interactive.movingHandler.id;
        if (isActive) renderPointHandler(context, area, isActive);
      } else {
        const isActive = area.id === interactive.hoverHandlerId;
        renderPointHandler(context, area, isActive);
      }
    });
  }

  const midControlPoints = getInteractiveEdgeHandlers(region, node);
  midControlPoints.forEach((area) => {
    if (interactive.movingHandler) {
      const isActive = area.id === interactive.movingHandler.id;
      if (isActive) renderEdgeHandler(context, area, isActive);
    } else {
      const isActive = area.id === interactive.hoverHandlerId;
      renderEdgeHandler(context, area, isActive);
    }
  });

  // section-move 底部中点句柄
  // 传递 scale=1/scaleProvider.scale，保持视觉恒定
  const sectionHandlers = getSectionMoveHandlers(stageContext, node, region, 1);
  sectionHandlers.forEach((area) => {
    if (interactive.movingHandler) {
      const isActive = area.id === interactive.movingHandler.id;
      if (isActive) renderSectionMoveHandler(context, area, isActive);
    } else {
      const isActive = area.id === interactive.hoverHandlerId;
      renderSectionMoveHandler(context, area, isActive);
    }
  });

  // 旋转手柄
  const rotateHandlers = getRotateHandlers(region, node);
  rotateHandlers.forEach((area) => {
    if (interactive.movingHandler) {
      const isActive = area.id === interactive.movingHandler.id;
      if (isActive) renderRotateHandler(context, area, isActive);
    } else {
      const isActive = area.id === interactive.hoverHandlerId;
      renderRotateHandler(context, area, isActive);
    }
  });

  // 绘制角度提示标签（旋转中或悬停旋转手柄时）
  if (rotateHandlers && rotateHandlers.length) {
    const area = rotateHandlers[0];
    const isRotating =
      (interactive.movingHandler && interactive.movingHandler.type === 'rotate') ||
      !!interactive.rotationState;
    const isHoveringRotate = interactive.hoverHandlerId === 'rotate';
    if (isRotating || isHoveringRotate) {
      const { action } = stageContext.providers;
      let deg = null;

      if (
        interactive.movingHandler &&
        interactive.movingHandler.type === 'rotate' &&
        interactive.movingHandler.rotate
      ) {
        const r = interactive.movingHandler.rotate;
        const curr = Math.atan2(interactive.y - r.center.y, interactive.x - r.center.x);
        const delta = curr - r.startAngle;
        deg = ((r.baseRotation + delta) * 180) / Math.PI;
        if (action.isShiftKeyPressed) deg = Math.round(deg / 15) * 15;
      } else if (interactive.rotationState) {
        const r = interactive.rotationState;
        const curr = Math.atan2(interactive.y - r.center.y, interactive.x - r.center.x);
        const delta = curr - r.startAngle;
        deg = ((r.baseRotation + delta) * 180) / Math.PI;
        if (action.isShiftKeyPressed) deg = Math.round(deg / 15) * 15;
      } else {
        // 非拖拽，仅悬停时显示当前区域角度
        deg = region && typeof region.angle === 'number' ? region.angle : 0;
      }

      // 归一化到 [-180, 180]
      const normalized = ((((deg + 180) % 360) + 360) % 360) - 180;
      const text = `${Math.round(normalized)}°`;

      // 位置：在旋转手柄外沿再偏移一定像素
      const center = area.center;
      const anchor = area.anchor || center;
      const vx = center.x - anchor.x;
      const vy = center.y - anchor.y;
      const len = Math.hypot(vx, vy) || 1;
      const nx = vx / len;
      const ny = vy / len;
      const baseOffset =
        typeof area.visualRadius === 'number' ? area.visualRadius : area.w ? area.w / 2 : 20;
      const offset = baseOffset + 32;
      const labelX = center.x + nx * offset;
      const labelY = center.y + ny * offset;

      drawAngleLabel(context, labelX, labelY, text);
    }
  }

  // 半径手柄（仅矩形形状）
  if (isRadiusableNode(node)) {
    const radiusHandlers = getInteractiveRadiusHandlers(stageContext, node, region, 1, scale.scale);
    radiusHandlers.forEach((area) => {
      const isActive =
        area.id === interactive.hoverHandlerId ||
        (interactive.movingHandler && area.id === interactive.movingHandler.id);
      renderRadiusHandler(context, area, isActive);
    });
  }

  // debug
  if (stageContext.debug) {
    getInteractiveHandlers(stageContext, node).forEach((area) => {
      if (area.type === 'point') {
        drawRegion(context, stageContext, area);
      } else {
        drawRegion(context, stageContext, area);
      }
    });
  }
}

function drawAngleLabel(context, x, y, text) {
  if (!text) return;
  context.save();

  // Text style
  const fontSize = 24;
  context.font = `${fontSize}px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif`;
  context.textBaseline = 'middle';
  context.textAlign = 'center';

  const metrics = context.measureText(text);
  const textWidth = Math.ceil(metrics.width);
  const paddingX = 8;
  const paddingY = 4;
  const boxWidth = textWidth + paddingX * 2;
  const boxHeight = fontSize + paddingY * 2;
  const r = Math.min(10, boxHeight / 2);

  // Background pill with subtle shadow
  context.shadowColor = 'rgba(0,0,0,0.25)';
  context.shadowBlur = 6;
  context.shadowOffsetY = 2;

  const left = x - boxWidth / 2;
  const top = y - boxHeight / 2;

  context.beginPath();
  // Rounded rect
  context.moveTo(left + r, top);
  context.lineTo(left + boxWidth - r, top);
  context.quadraticCurveTo(left + boxWidth, top, left + boxWidth, top + r);
  context.lineTo(left + boxWidth, top + boxHeight - r);
  context.quadraticCurveTo(left + boxWidth, top + boxHeight, left + boxWidth - r, top + boxHeight);
  context.lineTo(left + r, top + boxHeight);
  context.quadraticCurveTo(left, top + boxHeight, left, top + boxHeight - r);
  context.lineTo(left, top + r);
  context.quadraticCurveTo(left, top, left + r, top);

  context.fillStyle = 'rgba(17, 24, 39, 0.85)';
  context.fill();

  // Reset shadow for text
  context.shadowColor = 'transparent';
  context.shadowBlur = 0;
  context.shadowOffsetY = 0;

  // Text
  context.fillStyle = '#ffffff';
  context.fillText(text, x, y);

  context.restore();
}

export function hitTestNode(stageContext, point, node) {
  if (node.isSeedeBadge === true || node.pointerEvents === 'none') return false;
  // 特殊处理Line节点
  if (node.type === TYPES.line) {
    return hitTestLineNode(stageContext, point, node);
  }

  const region = stageContext.api.getNodeRegion(node);
  const isContains = region.contains(point.x, point.y);
  return isContains;
}

function hitTestNodeWithChildren(stageContext, point, node) {
  // 当节点被锁定时，仅命中测试该节点本身，不允许选择其子节点
  if (node.locked === true) {
    return hitTestNode(stageContext, point, node) ? node : null;
  }
  if (
    node.type !== TYPES.group &&
    node.type !== TYPES.frame &&
    node.type !== TYPES.col &&
    node.type !== TYPES.row
  ) {
    return hitTestNode(stageContext, point, node) ? node : null;
  } else {
    const children = stageContext.api.getChildren(node.id).reverse();
    for (const child of children) {
      if (hitTestNode(stageContext, point, child)) return child;
    }
    return hitTestNode(stageContext, point, node) ? node : null;
  }
}

function testNodeAndDescendants(stageContext, point, node) {
  // 若当前节点被锁定，仅测试节点本身，不再递归子节点
  if (node.locked === true) {
    return hitTestNode(stageContext, point, node) ? node : null;
  }
  // Recursive case: test all children first
  const children = stageContext.api.getChildren(node.id).reverse();
  for (const child of children) {
    const result = testNodeAndDescendants(stageContext, point, child);
    if (result) return result;
  }

  // If no children matched, test the node itself
  return hitTestNode(stageContext, point, node) ? node : null;
}

function createHitTestContext(point, screenPoint, type, stageContext, { skipIds = [] } = {}) {
  const interactive = stageContext.interactive;
  const deltaX = point.x - interactive.x;
  const deltaY = point.y - interactive.y;
  interactive.x = point.x;
  interactive.y = point.y;

  const screenDeltaX = screenPoint.x - interactive.screenX;
  const screenDeltaY = screenPoint.y - interactive.screenY;
  interactive.screenX = screenPoint.x;
  interactive.screenY = screenPoint.y;

  let targetNode = null;
  let shallowNode = null;
  let depthNode = null;
  let targetHandler = null;
  let targetRegion = null;
  let targetSelectedNode = null;

  const selectedNodes = stageContext.api.getSelectedNodes();
  const currentSelectableNodes = getCurrentSelectableNodes(stageContext, skipIds);
  const scrollbarHandlers = getScrollbarHandlers(stageContext);
  for (const handler of scrollbarHandlers) {
    if (handler.contains(screenPoint.x, screenPoint.y)) {
      targetHandler = handler;
      break;
    }
  }

  if (!targetHandler) {
    const { action } = stageContext.providers;
    const isMetaKeyPressed = action.isMetaKeyPressed;
    const nodes = currentSelectableNodes;
    const isMultiSelect = selectedNodes.length > 1;

    let isRegionMatch = false;
    if (isMultiSelect) {
      const rect = stageContext.api.getSelectedNodesRegion();
      const region = Region.fromRect(rect);
      const handler = hitTestNodeHandler(stageContext, point, null, region);
      if (handler) {
        targetHandler = handler;
        isRegionMatch = true;
      }
      if (!targetHandler) {
        isRegionMatch = rect.contains(point.x, point.y);
        if (isRegionMatch) {
          targetRegion = rect;
          targetRegion.nodes = selectedNodes;
        }
      }
    }

    if (!isRegionMatch) {
      const isDeepMode = isMetaKeyPressed || interactive.brush;
      if (!isDeepMode) {
        for (const node of selectedNodes) {
          if (skipIds.includes(node.id)) continue;
          const handler = hitTestNodeHandler(stageContext, point, node);
          if (handler) {
            targetHandler = handler;
            targetNode = node;
            break;
          } else {
            if (node.type === TYPES.frame) {
              const result = hitTestNodeWithChildren(stageContext, point, node);
              if (result) {
                targetNode = result;
                break;
              }
            } else {
              const result = hitTestNode(stageContext, point, node);
              if (result) {
                targetSelectedNode = node;
                break;
              }
            }
          }
        }
      } else {
        const topNodes = stageContext.api.getChildren(null);
        for (const node of topNodes) {
          const result = testNodeAndDescendants(stageContext, point, node);
          if (result) {
            targetNode = result;
            break;
          }
        }
      }

      if (!targetNode && !targetHandler) {
        for (const node of selectedNodes) {
          const handler = hitTestNodeHandler(stageContext, point, node);
          if (handler) {
            targetHandler = handler;
            targetNode = node;
            break;
          }
        }

        if (!targetNode) {
          for (const node of nodes) {
            const result = hitTestNode(stageContext, point, node);
            if (result) {
              targetNode = node;
              break;
            }
          }
        }
      }
    }

    if (!targetNode) {
      for (const node of nodes) {
        const result = hitTestNode(stageContext, point, node);
        if (result) {
          shallowNode = node;
          break;
        }
      }
    }

    for (const node of selectedNodes) {
      const result = hitTestNodeWithChildren(stageContext, point, node);
      if (result) {
        depthNode = result;
        break;
      }
    }
  }

  return {
    type,
    point,
    screenPoint,
    deltaX,
    deltaY,
    screenDeltaX,
    screenDeltaY,
    targetNode,
    shallowNode,
    depthNode,
    targetHandler,
    targetSelectedNode,
    targetRegion,
    selectedNodes,
    currentSelectableNodes,
    stageContext: stageContext,
    selection: stageContext.providers.selection,
    provider: stageContext.providers.interactive,
  };
}

// 新增：Line节点右键菜单处理
function handleLineRightClick(hitTestContext) {
  const node = hitTestContext.targetNode || hitTestContext.shallowNode;

  if (!node || node.type !== TYPES.line) return false;

  // 检查是否点击在线段中点上
  if (hitTestContext.targetHandler && hitTestContext.targetHandler.type === 'lineMidPoint') {
    addPointAtSegment(hitTestContext);
    return true;
  }

  return false;
}

// 新增：Path节点右键菜单处理（仅直线锚点路径）
function handlePathRightClick(hitTestContext) {
  const node = hitTestContext.targetNode || hitTestContext.shallowNode;
  if (!node || node.type !== TYPES.path) return false;

  if (hitTestContext.targetHandler && hitTestContext.targetHandler.type === 'pathMidPoint') {
    const segmentIndex = hitTestContext.targetHandler.segmentIndex;
    const contextNode = node;
    const { api } = hitTestContext.stageContext;
    api.updateNode(contextNode.id, {
      path: addPathPointAtSegment(contextNode.path, segmentIndex, false),
    });
    return true;
  }
  return false;
}
